import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash-es";
import config from "config";
import useAuthUser from "hooks/useAuthUser";
import useUnreadBadge from "hooks/useUnreadBadge";
import emitter, { EVENTS } from "lib/emitter";
import providers from "lib/providers";

export default function useController() {
  const { user } = useAuthUser();

  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [unreadThreadsCount, setUnreadThreadsCount] = useState(0);
  const showGoLiveButton: boolean = user?.role === config.userPerformerRole;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const openGoLiveModal = useCallback(() => setShowGoLiveModal(true), []);
  const closeGoLiveModal = useCallback(() => setShowGoLiveModal(false), []);

  const fetchUnreadCount = useCallback(() => {
    providers.MessageThreadsProvider.getUnreadMessageStatistic().then((stat) => {
      setUnreadThreadsCount(stat.unreadMessageCount ?? 0);
    }).catch(() => { /* ignore */ });
  }, []);

  // Debounce batches rapid events (e.g. multiple messages arriving) into one API call,
  // with leading: true so the first event triggers immediately (no 1s delay).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshUnreadCount = useCallback(
    debounce(fetchUnreadCount, 500, { leading: true, trailing: true, maxWait: 2000 }),
    [fetchUnreadCount]
  );

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, refreshUnreadCount);
    emitter.on(EVENTS.MESSAGES_MARKED_READ, refreshUnreadCount);
    emitter.on(EVENTS.THREADS_REFRESH, refreshUnreadCount);
    pollRef.current = setInterval(fetchUnreadCount, 15000);

    return () => {
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, refreshUnreadCount);
      emitter.off(EVENTS.MESSAGES_MARKED_READ, refreshUnreadCount);
      emitter.off(EVENTS.THREADS_REFRESH, refreshUnreadCount);
      refreshUnreadCount.cancel();
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshUnreadCount, fetchUnreadCount]);

  useUnreadBadge(unreadThreadsCount);

  const onClickMemberBalance = useCallback(() => emitter.emit(EVENTS.BALANCE_REFILL_SHOW), []);

  return {
    onClickMemberBalance,
    user,
    showGoLiveButton,
    showGoLiveModal,
    openGoLiveModal,
    closeGoLiveModal,
    unreadThreadsCount,
  };
}
